import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import {
    DocumentContext,
    DocumentOverviewItemDto,
    DocumentSortOrder,
    IDocumentStackItemDto,
    IRootOverviewItemDto,
    isFolder,
} from '../../../config/documentConfig'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import { CommandBar, CommandBarButton, IButtonStyles, ICommandBarItemProps, ISelection } from '@fluentui/react'
import { IFileDto } from '../../../config/fileConfig'
import ImportDocumentButton from './ImportDocumentButton'
import DialogButton from '../../Button/DialogButton'
import { DataPromise } from '../../../context/AxiosContext'
import useAxios from '../../../hooks/useAxios'
import {
    DocumentType,
    getDocumentType,
    getDownloadUrl,
    getDriveItemDownloadUrl,
    isConvertableToPdf,
} from '../../../helpers/documentHelper'
import { useHistory, useRouteMatch } from 'react-router-dom'
import ComposeEmailDialog, { ComposeEmailDialogProps } from '../../Dialog/OutlookItem/ComposeEmailDialog'
import { MainDto } from '../../../config/filePartyConfig'
import { DocumentMutationAction } from '../../Dialog/MutateDocument/MutateDocumentDialog'
import MutateDocumentButton from './MutateDocumentButton'
import OfficeDocumentButton from './OfficeDocumentButton'
import DeleteDocumentButton from './DeleteDocumentButton'
import CreateFolderButton from './CreateFolderButton'
import {
    DocumentGenerationContextData,
    DocumentGenerationStatusData,
    DocumentGenerationType,
} from '../../../hooks/document/useDocumentGenerationHandler'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { QueueData, QueueStatus } from '../../../hooks/useQueueStateHandler'
import { useTranslation } from 'react-i18next'
import { useSettings } from '../../../hooks/useSettings'
import { DocumentDto } from '../../../context/GlobalContext'
import { appInsightsReact } from '../../../pages/App'
import { SeverityLevel } from '@microsoft/applicationinsights-common'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import useRoles, { rolesTasks } from '../../../hooks/useRoles'

export enum DocumentSelectionType {
    None = 'NONE',
    Document = 'DOCUMENT',
    Multiple = 'MULTIPLE',
    Folder = 'FOLDER',
}

export type DocumentCommandBarItem = ICommandBarItemProps & { filter?: () => boolean }

type Props = {
    file: IFileDto
    parties: MainDto[]
    isLoading: boolean
    sortOrder: DocumentSortOrder
    setSortOrder: (sortOrder: DocumentSortOrder) => void
    rootFolder: DataPromise<IRootOverviewItemDto>
    selection: ISelection<DocumentOverviewItemDto>
    reset: (sortOrder: DocumentSortOrder, stack?: IDocumentStackItemDto[]) => void
}

export type DocumentCommandBarItemProps = Props & {
    selectionType: DocumentSelectionType
    selectedItems: DocumentOverviewItemDto[]
    item: DocumentCommandBarItem
}

export const overflowButtonStyles: IButtonStyles = {
    root: { width: '100%' },
    label: { textAlign: 'left', lineHeight: 36 },
}

export const visibleButtonStyles: IButtonStyles = {
    root: { height: '100%' },
}

type SortOrderItemProps = {
    sortOrder: Props['sortOrder']
    onClick(sortOrder: DocumentSortOrder): void
}

const getSortOrderItems = (props: SortOrderItemProps, translate: (key: string) => string): DocumentCommandBarItem[] => [
    {
        key: 'filter-a-z',
        text: translate('sorting.az'),
        canCheck: true,
        isChecked: props.sortOrder === DocumentSortOrder.NameAsc,
        onClick: () => props.onClick(DocumentSortOrder.NameAsc),
    },
    {
        key: 'filter-z-a',
        text: translate('sorting.za'),
        canCheck: true,
        isChecked: props.sortOrder === DocumentSortOrder.NameDesc,
        onClick: () => props.onClick(DocumentSortOrder.NameDesc),
    },
    {
        key: 'filter-old-new',
        text: translate('sorting.dateasc'),
        canCheck: true,
        isChecked: props.sortOrder === DocumentSortOrder.ModifiedAsc,
        onClick: () => props.onClick(DocumentSortOrder.ModifiedAsc),
    },
    {
        key: 'filter-new-old',
        text: translate('sorting.datedesc'),
        canCheck: true,
        isChecked: props.sortOrder === DocumentSortOrder.ModifiedDesc,
        onClick: () => props.onClick(DocumentSortOrder.ModifiedDesc),
    },
]

const filter = (item: DocumentCommandBarItem) => item.filter?.() ?? true

function removeFromSelection(props: Props, item: DocumentOverviewItemDto) {
    return () => props.selection.toggleKeySelected(item.id)
}

export default function DocumentCommandBar(props: Props): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const match = useRouteMatch()
    const { any: hasAnyRole } = useRoles()
    const { t: translate } = useTranslation()
    const settings = useSettings()

    const documentsTemplates = useMemo<DocumentDto[]>(
        () => (settings?.documents || []).filter(document => document.context === DocumentContext.Document),
        [settings?.documents],
    )

    const selectionType = useMemo(() => {
        if (props.selection.getSelectedCount() === 0) {
            return DocumentSelectionType.None
        } else if (props.selection.getSelectedCount() > 1) {
            return DocumentSelectionType.Multiple
        } else if (props.selection.getSelection().find(isFolder)) {
            return DocumentSelectionType.Folder
        } else {
            return DocumentSelectionType.Document
        }
    }, [props.selection.getSelectedIndices()])
    const selectedItems = props.selection.getSelection()
    const itemProps = { ...props, selectedItems, selectionType }
    const submitRequest = useSubmitDocumentGenerationRequest(
        DocumentGenerationType.PdfConversion,
        {
            [QueueStatus.Finished]: () => {
                props.reset(props.sortOrder)
            },
        },
        true,
    )

    const items = useMemo<ICommandBarItemProps[]>(
        () => [
            {
                key: 'add-attachment',
                iconProps: { iconName: 'MailAttached' },
                text: translate('file.pivot.document.add.attachment'),
                filter: () =>
                    isAppContext(AppContext.Compose) &&
                    [DocumentSelectionType.Document, DocumentSelectionType.Multiple].includes(selectionType),
                onClick: () =>
                    selectedItems.forEach(item =>
                        getDriveItemDownloadUrl(item.id, axios).then(downloadUrl => {
                            Office.context.mailbox.item.addFileAttachmentAsync(
                                downloadUrl,
                                item.name,
                                removeFromSelection(props, item),
                            )
                        }),
                    ),
            },
            {
                key: 'add-pdf',
                iconProps: { iconName: 'PDF' },
                text: translate('file.pivot.document.add.pdf'),
                disabled: !selectedItems.some(item => isConvertableToPdf(item.name)),
                filter: () =>
                    isOfficeInitialized() &&
                    isAppContext(AppContext.Compose) &&
                    [DocumentSelectionType.Document, DocumentSelectionType.Multiple].includes(selectionType) &&
                    !selectedItems.every(item => getDocumentType(item.name) === DocumentType.Reader),
                onClick: () => {
                    selectedItems.forEach(async item => {
                        if (isConvertableToPdf(item.name)) {
                            const payload = {
                                itemId: item.id,
                                fileName: item.name,
                                folderId: item.parentId,
                            }
                            submitRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'), {
                                [QueueStatus.Finished]: (
                                    e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>,
                                ) => {
                                    if (Office.context.mailbox.item.addFileAttachmentAsync) {
                                        Office.context.mailbox.item.addFileAttachmentAsync(
                                            getDownloadUrl(e.status.driveItem),
                                            e.status.driveItem.name,
                                            removeFromSelection(props, item),
                                        )
                                    } else {
                                        const error = new Error(
                                            translate('file.pivot.document.error.outdated_outlook', {
                                                host: Office.context.diagnostics.host,
                                                version: Office.context.diagnostics.version,
                                                platform: Office.context.diagnostics.platform,
                                            }),
                                        )
                                        appInsightsReact.trackException({
                                            exception: error,
                                            severityLevel: SeverityLevel.Error,
                                            properties: {
                                                reactComponent: 'DocumentCommandBar',
                                                emailId: Office.context.mailbox?.item?.itemId,
                                            },
                                        })
                                        throw error
                                    }
                                },
                            })
                        } else {
                            const downloadUrl = await getDriveItemDownloadUrl(item.id, axios)
                            Office.context.mailbox.item.addFileAttachmentAsync(
                                downloadUrl,
                                item.name,
                                removeFromSelection(props, item),
                            )
                        }
                    })
                    props.reset(props.sortOrder)
                },
            },
            {
                key: 'create-email',
                iconProps: { iconName: 'OutlookLogo' },
                text: translate('file.pivot.document.create.email'),
                filter: () =>
                    isAppContext([AppContext.Read, AppContext.Module]) &&
                    [DocumentSelectionType.Document, DocumentSelectionType.Multiple].includes(selectionType),
                onRender: item => (
                    <DialogButton<ComposeEmailDialogProps>
                        button={CommandBarButton}
                        buttonProps={{
                            text: translate('file.pivot.document.create.email'),
                            iconProps: { iconName: 'OutlookLogo' },
                            styles: item.renderedInOverflow ? overflowButtonStyles : visibleButtonStyles,
                        }}
                        dialog={ComposeEmailDialog}
                        dialogProps={{
                            file: props.file,
                            parties: props.parties,
                            documents: selectedItems,
                            callback: () => props.selection.setAllSelected(false),
                        }}
                    />
                ),
            },
            {
                key: 'convert-pdf',
                iconProps: { iconName: 'PDF' },
                text: translate('file.pivot.document.create.pdf.convert'),
                filter: () =>
                    [DocumentSelectionType.Document, DocumentSelectionType.Multiple].includes(selectionType) &&
                    selectedItems.every(item => isConvertableToPdf(item.name)),
                onClick: () => {
                    selectedItems.forEach(item => {
                        if (isConvertableToPdf(item.name)) {
                            const payload = {
                                itemId: item.id,
                                fileName: item.name,
                                folderId: item.parentId,
                            }
                            submitRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'))
                        }
                    })
                    props.reset(props.sortOrder)
                },
            },
            {
                key: 'create-task',
                iconProps: { iconName: 'TaskLogo' },
                text: translate('file.pivot.document.create.task'),
                filter: () =>
                    hasAnyRole(rolesTasks) &&
                    [DocumentSelectionType.Document, DocumentSelectionType.Multiple].includes(selectionType),
                onClick: () =>
                    history.push(`${match.url}/task/add`, {
                        attachmentIds: selectedItems.map(item => item.id),
                    }),
            },
            {
                key: 'rename-document',
                filter: () => [DocumentSelectionType.Document, DocumentSelectionType.Folder].includes(selectionType),
                onRender: item => (
                    <MutateDocumentButton action={DocumentMutationAction.Rename} item={item} {...itemProps} />
                ),
            },
            {
                key: 'copy-document',
                filter: () => selectionType !== DocumentSelectionType.None,
                onRender: item => (
                    <MutateDocumentButton action={DocumentMutationAction.Copy} item={item} {...itemProps} />
                ),
            },
            {
                key: 'move-document',
                filter: () => selectionType !== DocumentSelectionType.None,
                onRender: item => (
                    <MutateDocumentButton action={DocumentMutationAction.Move} item={item} {...itemProps} />
                ),
            },
            {
                key: 'delete-document',
                filter: () => selectionType !== DocumentSelectionType.None,
                onRender: (item: DocumentCommandBarItem) => <DeleteDocumentButton item={item} {...itemProps} />,
            },
            {
                key: 'add-word',
                filter: () =>
                    [DocumentSelectionType.None, DocumentSelectionType.Folder].includes(selectionType) &&
                    documentsTemplates.some(document => document.type === DocumentType.Word),
                onRender: item => <OfficeDocumentButton name="Word" item={item} {...itemProps} />,
            },
            {
                key: 'add-excel',
                filter: () =>
                    [DocumentSelectionType.None, DocumentSelectionType.Folder].includes(selectionType) &&
                    documentsTemplates.some(document => document.type === DocumentType.Excel),
                onRender: item => <OfficeDocumentButton name="Excel" item={item} {...itemProps} />,
            },
            {
                key: 'add-pdf',
                filter: () =>
                    [DocumentSelectionType.None, DocumentSelectionType.Folder].includes(selectionType) &&
                    documentsTemplates.some(document => document.type === DocumentType.PDF),
                onRender: item => <OfficeDocumentButton name="PDF" item={item} {...itemProps} />,
            },
        ],
        [itemProps],
    )

    const farItems = useMemo<ICommandBarItemProps[]>(
        () => [
            {
                key: 'sort-documents-button',
                iconProps: { iconName: 'Sort' },
                subMenuProps: {
                    items: getSortOrderItems(
                        {
                            sortOrder: props.sortOrder,
                            onClick: (sortOrder: DocumentSortOrder) => {
                                props.setSortOrder(sortOrder)
                                props.reset(sortOrder, [
                                    ...props.rootFolder.data.stack,
                                    { id: props.rootFolder.data.id, name: props.rootFolder.data.name },
                                ])
                            },
                        },
                        translate,
                    ),
                },
            },
            {
                key: 'import-documents',
                filter: () => [DocumentSelectionType.None, DocumentSelectionType.Folder].includes(selectionType),
                onRender: () => (
                    <ImportDocumentButton
                        folders={props.rootFolder.data}
                        selectedFolder={selectedItems.find(isFolder)}
                    />
                ),
            },
            {
                key: 'create-sub-folder',
                filter: () => [DocumentSelectionType.None, DocumentSelectionType.Folder].includes(selectionType),
                onRender: item => <CreateFolderButton item={item} {...itemProps} />,
            },
            {
                key: 'refresh-documents-button',
                text: translate('file.pivot.document.refresh'),
                iconProps: { iconName: 'FabricSyncFolder' },
                iconOnly: true,
                onClick: () => {
                    props.reset(props.sortOrder, [
                        ...props.rootFolder.data.stack,
                        { id: props.rootFolder.data.id, name: props.rootFolder.data.name },
                    ])
                },
            },
        ],
        [itemProps],
    )

    // Make sure that the commandbar is always updated if one of the items changed.
    return useMemo(
        () => (
            <CommandBar
                items={items.filter(filter)}
                farItems={farItems.filter(filter)}
                styles={{ root: { padding: 0 } }}
            />
        ),
        [itemProps],
    )
}
